import * as THREE from "three";
import { RESOLUTION } from "./config";
import { PostPipeline, type BlurOptions, type ViewFrame } from "./post";
import type { AccentOptions, DitherOptions } from "./types";

export class Presenter {
  readonly renderer: THREE.WebGLRenderer;
  readonly post: PostPipeline;

  private readonly targetHeight: number;
  private width = 1;
  private height = 1;

  constructor(
    canvas: HTMLCanvasElement,
    dither: DitherOptions,
    blur: BlurOptions,
    resolutionScale: number,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(1);
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.BasicShadowMap;
    this.renderer.shadowMap.autoUpdate = false;

    this.targetHeight = THREE.MathUtils.clamp(
      Math.round(RESOLUTION.targetHeight * resolutionScale),
      RESOLUTION.minHeight,
      RESOLUTION.maxHeight,
    );

    this.post = new PostPipeline(this.renderer, dither, blur, 16, 9);
  }

  resize(cssWidth: number, cssHeight: number): void {
    const aspect = Math.max(0.1, cssWidth / Math.max(1, cssHeight));
    const height = this.targetHeight;
    const width = Math.max(16, Math.round(height * aspect));

    this.width = width;
    this.height = height;
    this.renderer.setSize(width, height, false);
    this.post.setSize(width, height);
  }

  setAccent(accent: Partial<AccentOptions>): void {
    this.post.setAccent(accent);
  }

  attachExplored(texture: THREE.Texture): void {
    this.post.attachExplored(texture);
  }

  setView(frame: ViewFrame): void {
    this.post.setView(frame);
  }

  render(scene: THREE.Scene, camera: THREE.Camera, accentStrength: number): void {
    this.post.render(scene, camera, accentStrength);
  }

  get bufferSize(): [number, number] {
    return [this.width, this.height];
  }
}
